// hooks

// libraries
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { DealState, StateTree } from '@makemydeal/dr-dash-types';
import * as dealEffectiveSelectors from '@makemydeal/dr-dash-store/selectors/dealEffectiveSelectors';

// consts
import * as constants from '../../constants';

// components
import DealSummaryItem from '../common/DealSummaryItem';

import { useDealSummary } from '../../common/DealSummaryProvider';

// styles
import { detailedViewValueStyles } from '../utils/styles';
import { useTrackedSelector } from '../../telemetry/useTrackedSelector';

export type TotalAmountDueProps = DealState & {
    showLargeValue?: boolean;
};

const AmountFinanced = ({ deal, showLargeValue }: TotalAmountDueProps) => {
    // This read CLOSES the finance breakdown identity
    // (netSelling - netTrade - rebates - cashDown == amtFinanced), so it carries
    // the four inputs with it as `ctx.*` (US2058402). Amount financed is the last
    // term to settle — the payment service answers, this value changes, and at
    // that instant every input is final — which is exactly why the companions
    // belong here and not on an input. Attaching them mid-recalc would record a
    // variance the app was about to fix on its own.
    //
    // Without this the identity was only checkable on page load: emission is
    // change-only, so a Cash Down edit emits `effectiveDownPayment` at one action
    // and `effectiveAmountFinanced` at the next while the three untouched terms
    // stay silent, and NRQL cannot carry a value forward across events.
    //
    // `contextArg: deal` keeps the companions in the same scope as the value —
    // an offer-compare column would otherwise pair a historical deal's amount
    // financed with the primary offer's inputs.
    const financeTotalAmountDue = useTrackedSelector(
        (state: StateTree) => dealEffectiveSelectors.selectEffectiveAmountFinanced(state, deal),
        {
            component: 'AmountFinanced',
            field: 'effectiveAmountFinanced',
            context: 'financeBreakdown',
            contextArg: deal,
            // Emit the companion dwell row (US2119069). A variance that flashed
            // for 900ms during a recalc and one the customer stared at for four
            // minutes are indistinguishable on the read alone, because a value's
            // on-screen time is only knowable once the next value replaces it.
            emitDwell: true
        }
    );
    const { showDetailedView } = useDealSummary();

    return (
        <DealSummaryItem
            label={constants.TOTAL_AMOUNT_DUE_FINANCE}
            value={formatDollarsAndCents(financeTotalAmountDue)}
            showLargeValue={showLargeValue}
            valueStyles={showDetailedView ? detailedViewValueStyles : {}}
        />
    );
};

export default AmountFinanced;
